/**
 * API configuration - base URLs and endpoint routes
 * Base URL: /v1
 * All API routes in one place for easy maintenance
 */

// const appUrl = "http://localhost:3003";
const appUrl = "https://backend.zillionexchange.com";

export const imageUrl = appUrl//"https://backend.zillion.wrathcode.com";

export const ApiConfig = {
  // ========= Base URLs =========
  baseUrl: `${appUrl}/`,
  imageUrl,
  baseAdmin: `${appUrl}/v1/admin/`,
  baseAdminAuth: `${appUrl}/v1/admin/auth/`,

  // ========= Admin Auth Endpoints =========
  authStep1: "step1",
  authStep2: "step2",
  authResendOtp: "resend-otp",

  // ========= Admin Logs =========
  adminLogs: "logs",

  // ========= Sub-Admins (master admin only) =========
  subAdmins: "sub-admins",
  subAdminById: (subAdminId) => `sub-admins/${subAdminId}`,
  subAdminStatus: (subAdminId) => `sub-admins/${subAdminId}/status`,
  subAdminPermissions: "sub-admins/permissions",

  // ========= Dashboard =========
  dashboardCounts: "dashboard/counts",

  // ========= Users =========
  adminUsers: "users",
  /** GET /v1/admin/users — query: page, limit, search, kyc_status, status, country_code */
  /** GET /v1/admin/users/blocked — query: page, limit, search, kyc_status, account_status, country_code */
  usersBlockedList: "users/blocked",
  /** GET /v1/admin/users/unverified — query: page, limit, search, kyc_status, status */
  usersUnverifiedList: "users/unverified",
  userDetail: (id) => `users/${id}`,
  userStatus: "users/status",
  /** PUT — body: { userId, status: "Active" | "Blocked" } */
  userUnblock: "users/unblock",
  /** DELETE /v1/admin/users/account — body: { userId, confirm: "DELETE" } */
  usersDeleteAccount: "users/account",
  /** PUT — body: { userId, payout_status: "active" | "inactive" } */
  userPayoutStatus: "users/payout-status",
  /** PUT /v1/admin/users/contact — body: { userId, emailId?, mobileNumber?, country_code? } */
  usersContact: "users/contact",
  /** PUT /v1/admin/users/brokerage-fee — body: { userId, brokerageFee: boolean } */
  usersBrokerageFee: "users/brokerage-fee",
  /** PUT /v1/admin/users/withdrawal-status — body: { userId, withdrawalEnabled: boolean } */
  usersWithdrawalStatus: "users/withdrawal-status",
  userKyc: (id) => `users/${id}/kyc`,
  kycList: "kyc",
  /** GET /v1/admin/kyc — query: status, page, limit, search, country_code */
  // KYC (centralized routes - body includes userId)
  kycApprove: "kyc/approve",
  kycReject: "kyc/reject",
  kycApproveDocument: "kyc/approve-document",
  kycRejectDocument: "kyc/reject-document",
  userWallets: (id) => `users/${id}/wallets`,
  userTransactions: (id) => `users/${id}/transactions`,
  userInternalTransfers: (id) => `users/${id}/internal-transfers`,
  /** GET /v1/admin/users/:userId/internal-transfer-settings — userId is Mongo _id (24 hex) */
  userInternalTransferSettings: (userId) => `users/${userId}/internal-transfer-settings`,
  /** PUT /v1/admin/users/internal-transfer-settings — one-toggle or save-all body */
  usersInternalTransferSettings: "users/internal-transfer-settings",
  userSpotTrades: (id) => `users/${id}/spot-trades`,
  userSpotOrders: (id) => `users/${id}/spot-orders`,
  userSwapHistory: (id) => `users/${id}/swap-history`,
  userEarningPlans: (id) => `users/${id}/earning-plans`,
  userLaunchpad: (id) => `users/${id}/launchpad`,
  userSupportTickets: (id) => `users/${id}/support-tickets`,
  userReferral: (id) => `users/${id}/referral`,
  /** GET /v1/admin/users/commission-report — query: page, limit, search, account_status, from, to, commission_type, package, referral_level, sort_by, sort_order */
  usersCommissionReport: "users/commission-report",
  /** GET /v1/admin/users/:userId/commission-report */
  userCommissionReport: (userId) => `users/${userId}/commission-report`,
  /** GET /v1/admin/users/:userId/staking-details */
  userCommissionStakingDetails: (userId) => `users/${userId}/staking-details`,
  /** GET /v1/admin/users/:userId/trading-details */
  userCommissionTradingDetails: (userId) => `users/${userId}/trading-details`,
  /** GET /v1/admin/users/:userId/downline-commission */
  userCommissionDownline: (userId) => `users/${userId}/downline-commission`,
  /** GET /v1/admin/users/:userId/upline-commission */
  userCommissionUpline: (userId) => `users/${userId}/upline-commission`,
  userP2pOrders: (id) => `users/${id}/p2p-orders`,
  userP2pAds: (id) => `users/${id}/p2p-ads`,
  userP2pPaymentMethods: (id) => `users/${id}/p2p-payment-methods`,
  userFuturesTrades: (id) => `users/${id}/futures-trades`,
  userFuturesOrders: (id) => `users/${id}/futures-orders`,
  userFuturesPositions: (id) => `users/${id}/futures-positions`,

  // ========= Copy Trading (admin) =========
  /** GET /v1/admin/copy-trading/master-traders — query: page, limit, search, role (master|none|all) */
  copyTradingMasterTraders: "copy-trading/master-traders",
  /** GET /v1/admin/copy-trading/master-applications — query: page, limit, search, status */
  copyTradingMasterApplications: "copy-trading/master-applications",
  /** GET /v1/admin/copy-trading/master-applications/:applicationId */
  copyTradingMasterApplicationById: (applicationId) =>
    `copy-trading/master-applications/${applicationId}`,
  /** PUT /v1/admin/copy-trading/master-applications/:applicationId/status */
  copyTradingMasterApplicationStatus: (applicationId) =>
    `copy-trading/master-applications/${applicationId}/status`,
  /** PUT /v1/admin/copy-trading/master-trade-account — body: { userId, isMasterAccount } */
  copyTradingMasterTradeAccount: "copy-trading/master-trade-account",
  /** GET/PUT /v1/admin/copy-trading/masters/:masterId/configuration */
  copyTradingMasterConfiguration: (masterId) =>
    `copy-trading/masters/${masterId}/configuration`,
  /** GET /v1/admin/copy-trading/masters/:masterId/followers — query: page, limit, status */
  copyTradingMasterFollowers: (masterId) =>
    `copy-trading/masters/${masterId}/followers`,
  /** GET /v1/admin/copy-trading/masters/:masterId/trades — query: market, page, limit, status */
  copyTradingMasterTrades: (masterId) =>
    `copy-trading/masters/${masterId}/trades`,
  /** GET /v1/admin/copy-trading/masters/:masterId/pnl */
  copyTradingMasterPnl: (masterId) =>
    `copy-trading/masters/${masterId}/pnl`,
  /** GET /v1/admin/copy-trading/masters/:masterId/copied-trades — query: page, limit */
  copyTradingMasterCopiedTrades: (masterId) =>
    `copy-trading/masters/${masterId}/copied-trades`,
  /** GET /v1/admin/copy-trading/masters/:masterId/trades/pnl — query: market, page, limit */
  copyTradingMasterTradesPnl: (masterId) =>
    `copy-trading/masters/${masterId}/trades/pnl`,
  /** GET /v1/admin/copy-trading/masters/:masterId/followers/pnl — query: status (ACTIVE|PAUSED|STOPPED|all) */
  copyTradingMasterFollowersPnl: (masterId) =>
    `copy-trading/masters/${masterId}/followers/pnl`,
  /** GET /v1/admin/copy-trading/snapshots — query: scope, masterId, followerId, page, limit */
  copyTradingSnapshots: "copy-trading/snapshots",
  /** GET /v1/admin/copy-trading/masters/risk-flags */
  copyTradingRiskFlags: "copy-trading/masters/risk-flags",
  /** PUT /v1/admin/copy-trading/masters/:masterId/risk/release-total-stop */
  copyTradingReleaseTotalStop: (masterId) =>
    `copy-trading/masters/${masterId}/risk/release-total-stop`,
  /** GET /v1/admin/copy-trading/alerts — query: masterId, acknowledged, page, limit */
  copyTradingAlerts: "copy-trading/alerts",
  /** POST /v1/admin/copy-trading/alerts/:alertId/acknowledge */
  copyTradingAcknowledgeAlert: (alertId) =>
    `copy-trading/alerts/${alertId}/acknowledge`,
  /** GET /v1/admin/copy-trading/settlements — query: masterId, followerId, page, limit */
  copyTradingSettlements: "copy-trading/settlements",
  /** POST /v1/admin/copy-trading/settlements/run — body: {} | { masterId } | { masterId, followerId } */
  copyTradingSettlementsRun: "copy-trading/settlements/run",
  /** GET /v1/admin/copy-trading/masters/:masterId/wind-down */
  copyTradingMasterWindDown: (masterId) =>
    `copy-trading/masters/${masterId}/wind-down`,
  /** GET /v1/admin/copy-trading/reconciliation — query: resolved, masterId, page, limit */
  copyTradingReconciliation: "copy-trading/reconciliation",
  /** POST /v1/admin/copy-trading/reconciliation/run — body: { sinceMinutes? } */
  copyTradingReconciliationRun: "copy-trading/reconciliation/run",
  /** GET /v1/admin/copy-trading/masters/:masterId/config-versions — query: page, limit */
  copyTradingMasterConfigVersions: (masterId) =>
    `copy-trading/masters/${masterId}/config-versions`,
  /** GET /v1/admin/copy-trading/masters/:masterId/grandfathered-followers */
  copyTradingMasterGrandfatheredFollowers: (masterId) =>
    `copy-trading/masters/${masterId}/grandfathered-followers`,
  /** GET /v1/admin/copy-trading/surveillance — query: masterId, page, limit */
  copyTradingSurveillance: "copy-trading/surveillance",

  // ========= Global Spot =========
  globalSpotOrders: "spot/orders",
  globalSpotTrades: "spot/trades",

  // ========= Global Futures (admin) =========
  globalFuturesOrders: "futures/orders",
  globalFuturesPositions: "futures/positions",
  globalFuturesTrades: "futures/trades",

  /** GET /v1/cobo/admin/deposit-history — query: page, limit, userId, status, assetId, chainId, txHash */
  coboAdminDepositHistory: `${appUrl}/api/v1/cobo/admin/deposit-history`,
  /** GET /v1/cobo/admin/deposit-summary-by-currency — query: status, credited, assetId, chainId, from, to */
  coboAdminDepositSummaryByCurrency: `${appUrl}/api/v1/cobo/admin/deposit-summary-by-currency`,
  /** GET /v1/cobo/admin/withdrawal-history — query: page, limit, userId, status, coin, chainId, txHash */
  coboAdminWithdrawalHistory: `${appUrl}/api/v1/cobo/admin/withdrawal-history`,
  /** POST /v1/cobo/withdrawal — body: { adminApproved, withdrawalId, rejectedReason? } */
  coboWithdrawalAction: `${appUrl}/api/v1/cobo/withdrawal`,

  // ========= Global wallet (admin) =========
  walletDeposits: "wallet/deposits",
  walletGasFeeTransfers: "wallet/gas-fee-transfers",
  walletHotWalletTransfers: "wallet/hot-wallet-transfers",

  // ========= Coins =========
  coinsConfig: "coins/config",
  coinsList: "coins/list",
  /** GET /v1/admin/coins/contract-details — per-currency chain contract addresses & decimals */
  coinsContractDetails: "coins/contract-details",
  /** GET /v1/admin/get-coin — full coin documents (admin debit/credit dropdowns) */
  getCoin: "get-coin",
  coinsCreate: "coins",
  coinsUpdate: (coinId) => `coins/${coinId}`,

  // ========= Earning / Staking (admin) =========
  earningPackages: "earning/packages",
  earningPackageById: (packageId) => `earning/packages/${packageId}`,
  earningUserPlans: "earning/user-plans",
  /** GET /v1/admin/earning/user-plans/cancelled — query: page, limit, user, currency_id, plan_id, from, to */
  earningUserPlansCancelled: "earning/user-plans/cancelled",
  /** PUT /v1/admin/earning/user-plans/:subscriptionId */
  earningUserPlanById: (subscriptionId) => `earning/user-plans/${subscriptionId}`,
  /** GET /v1/admin/earning/user-plans/admin-for-user — admin-assigned earning subscriptions */
  earningUserPlansAdminForUser: "earning/user-plans/admin-for-user",
  /** POST /v1/admin/earning/user-plans/payout-history — body: { userId, stakingId } */
  earningUserPlanPayoutHistory: "earning/user-plans/payout-history",
  /** GET /v1/admin/earning/user-plans/:subscriptionId/edit-history */
  earningUserPlanEditHistory: (subscriptionId) => `earning/user-plans/${subscriptionId}/edit-history`,
  /** PUT /v1/admin/earning/user-plans/:subscriptionId/payout-status — body: { payout_status, userId? } */
  earningUserPlanPayoutStatus: (subscriptionId) =>
    `earning/user-plans/${subscriptionId}/payout-status`,
  /** GET /v1/admin/earning/check-existing-stake?emailId=...&planId=... */
  earningCheckExistingStake: "earning/check-existing-stake",
  /** GET /v1/admin/earning/search-users — query: q or search, limit (max 20) */
  earningSearchUsers: "earning/search-users",
  /** GET /v1/admin/earning/staking-full-report — list: page, limit(max 20), user, status, currency_id, plan_id, from, to; detail: subscription_id */
  earningStakingFullReport: "earning/staking-full-report",
  /** GET /v1/admin/earning/staking-statement — query: page, limit(max 50), user, status, currency_id, plan_id, from, to */
  earningStakingStatement: "earning/staking-statement",
  /** GET /v1/admin/earning/staking-statement/:userId — query: status, currency_id, plan_id, subscription_id, from, to, include_pending, statement_page, statement_limit */
  earningStakingStatementByUser: (userId) => `earning/staking-statement/${userId}`,

  /** POST /v1/earning/subscribe-earning-package-by-admin-for-user — body: planId, investAmount, walletType, emailId, type */
  earningSubscribePackageByAdminForUser: `${appUrl}/v1/earning/subscribe-earning-package-by-admin-for-user`,

  /** POST /v1/earning/cancel-subscription-by-admin — body: { stakingId, userId } */
  earningCancelSubscriptionByAdmin: `${appUrl}/v1/earning/cancel-subscription-by-admin`,

  // ========= Platform =========
  platformConfig: "platform",
  platformCancelSchedule: (feature) => `platform/${feature}/schedule`,
  otherSettings: "other-settings",
  /** GET /v1/admin/referral/referrers — query: page, limit, search */
  referralReferrers: "referral/referrers",
  /** GET /v1/admin/referral/users-commission-overview — query: page, limit, search, account_status, kyc_status, userId */
  referralUsersCommissionOverview: "referral/users-commission-overview",
  /** GET /v1/admin/referral/reward-logs — query: page, limit, search */
  referralRewardLogs: "referral/reward-logs",
  referralUserSummary: (userId) => `referral/user/${userId}`,
  referralUserInvitees: (userId) => `referral/user/${userId}/invitees`,
  referralUserEarningLogs: (userId) => `referral/user/${userId}/earning-logs`,
  /** GET /v1/admin/referral/user/:userId/network — query: level (1–5, optional) */
  referralUserNetwork: (userId) => `referral/user/${userId}/network`,
  /** GET /v1/admin/referral/downline-view/search — query: search */
  downlineViewSearch: "referral/downline-view/search",
  /** GET /v1/admin/referral/downline-view/:userId — query: level (1–5, optional) */
  downlineView: (userId) => `referral/downline-view/${userId}`,
  /** GET /v1/admin/referral/downline-view/:userId/export — query: level (1–5, optional) */
  downlineViewExport: (userId) => `referral/downline-view/${userId}/export`,

  /** GET /v1/admin/referral/signup-task-progress — query: page, limit, search */
  referralSignupTaskProgress: "referral/signup-task-progress",

  /** GET /v1/admin/referral/reward-details — query: page, limit, search */
  referralRewardDetails: "referral/reward-details",

  /** POST /v1/admin/referral/user/:userId/approve-funds — body: { mode: "instant" | "vesting" } */
  referralApproveFunds: (userId) => `referral/user/${userId}/approve-funds`,

  /** GET /v1/admin/brokerage/earners — query: page, limit */
  brokerageEarners: "brokerage/earners",
  /** GET /v1/admin/brokerage/earners/:userId — query: page, limit, level, currency_id */
  brokerageEarnerById: (userId) => `brokerage/earners/${userId}`,

  /** GET/POST /v1/admin/referral-release-plans */
  referralReleasePlans: "referral-release-plans",
  /** GET/PUT/DELETE /v1/admin/referral-release-plans/:planId */
  referralReleasePlanById: (planId) => `referral-release-plans/${planId}`,
  /** POST /v1/admin/referral-release-plans/release-round-now — body: { round, ignoreMonthWindow? } */
  referralReleaseRoundNow: "referral-release-plans/release-round-now",
  /** GET /v1/admin/referral-release-plans/release-round-now/status */
  referralReleaseRoundNowStatus: "referral-release-plans/release-round-now/status",
  /** GET /v1/admin/referral-release-plans/users — query: page, limit(max 50), user, status */
  referralReleasePlanUsers: "referral-release-plans/users",
  /** GET /v1/admin/referral-release-plans/users/:userId */
  referralReleasePlanUserById: (userId) => `referral-release-plans/users/${userId}`,
  /** GET /v1/admin/referral-release-plans/round-reports — query: round (required), page, limit(max 100), user */
  referralReleaseRoundReports: "referral-release-plans/round-reports",

  // ========= Pairs =========
  pairInfo: "pairs/pair-info",
  pairsCreate: "pairs",
  pairsList: "pairs",
  pairStatus: (pairId) => `pairs/${pairId}/status`,
  pairUpdate: (pairId) => `pairs/${pairId}`,
  futuresPairInfo: "futures-pairs/pair-info",
  futuresPairsCreate: "futures-pairs",

  // ========= Exchange (admin, full URL) =========
  /** GET http://localhost:6003/admin/exchange/trading-commission */
  adminExchangeTradingCommission: `${appUrl}/admin/exchange/trading-commission`,
  /** GET http://localhost:6003/admin/exchange/withdrawal-fees */
  adminExchangeWithdrawalFees: `${appUrl}/admin/exchange/withdrawal-fees`,
  /** GET http://localhost:6003/admin/exchange/p2p-fees */
  adminExchangeP2pFees: `${appUrl}/admin/exchange/p2p-fees`,
  /** GET http://localhost:6003/admin/exchange/futures-commission */
  adminExchangeFuturesCommission: `${appUrl}/admin/exchange/futures-commission`,
  /** GET http://localhost:6003/admin/exchange/all-wallet-types */
  adminExchangeAllWalletTypes: `${appUrl}/admin/exchange/all-wallet-types`,
  /** GET http://localhost:6003/admin/exchange/all-coins */
  adminExchangeAllCoins: `${appUrl}/admin/exchange/all-coins`,
  /** POST http://localhost:6003/admin/exchange/all-wallet-details-for-given-coin */
  adminExchangeWalletDetailsByCoin: `${appUrl}/admin/exchange/all-wallet-details-for-given-coin`,
  /** POST /v1/admin/exchange/debit-credit-by-admin */
  adminExchangeDebitCreditByAdmin: `${appUrl}/v1/admin/exchange/debit-credit-by-admin`,
  /** POST /v1/admin/exchange/cancel-order — body: { order_id } */
  adminExchangeCancelOrder: `${appUrl}/v1/admin/exchange/cancel-order`,
  /** GET http://localhost:6003/admin/exchange/all-admin-debit-credit-transactions */
  adminExchangeAllAdminDebitCreditTransactions: `${appUrl}/admin/exchange/all-admin-debit-credit-transactions`,
  /** POST http://localhost:6003/admin/exchange/add-announcement-category */
  adminExchangeAddAnnouncementCategory: `${appUrl}/admin/exchange/add-announcement-category`,
  /** GET http://localhost:6003/admin/exchange/announcement-category-listing */
  adminExchangeAnnouncementCategoryListing: `${appUrl}/admin/exchange/announcement-category-listing`,
  /** POST http://localhost:6003/admin/exchange/create-new-announcement */
  adminExchangeCreateNewAnnouncement: `${appUrl}/admin/exchange/create-new-announcement`,
  /** GET http://localhost:6003/admin/exchange/all-announcement-list-admin */
  adminExchangeAllAnnouncementListAdmin: `${appUrl}/admin/exchange/all-announcement-list-admin`,
  /** POST http://localhost:6003/admin/exchange/update-announcement-status */
  adminExchangeUpdateAnnouncementStatus: `${appUrl}/admin/exchange/update-announcement-status`,
  /** DELETE http://localhost:6003/admin/exchange/delete-announcement/:id */
  adminExchangeDeleteAnnouncement: (id) => `${appUrl}/admin/exchange/delete-announcement/${id}`,
  /** POST http://localhost:6003/admin/system/find-user-by-email-for-notification */
  adminSystemFindUserByEmailForNotification: `${appUrl}/admin/notification/find-user-by-email-for-notification`,
  /** POST http://localhost:6003/admin/notification/send-notification-to-single-user */
  adminNotificationSendToSingleUser: `${appUrl}/admin/notification/send-notification-to-single-user`,
  /** POST http://localhost:6003/admin/notification/send-notification-to-bulk-users */
  adminNotificationSendToBulkUsers: `${appUrl}/admin/notification/send-notification-to-bulk-users`,
  /** POST http://localhost:6003/admin/notification/send-notification-to-all */
  adminNotificationSendToAll: `${appUrl}/admin/notification/send-notification-to-all`,
  /** GET http://localhost:6003/admin/notification/get-all-notification-list */
  adminNotificationGetAllList: `${appUrl}/admin/notification/get-all-notification-list`,
  /** POST http://localhost:6003/admin/notification/update-notification-status */
  adminNotificationUpdateStatus: `${appUrl}/admin/notification/update-notification-status`,
  /** DELETE http://localhost:6003/admin/notification/delete-notification/:id */
  adminNotificationDelete: (id) => `${appUrl}/admin/notification/delete-notification/${id}`,
  /** POST http://localhost:6003/admin/apk/update-apk */
  adminApkUpdate: `${appUrl}/admin/apk/update-apk`,
  /** GET http://localhost:6003/admin/apk/get-apk */
  adminApkGet: `${appUrl}/admin/apk/get-apk`,
  /** POST http://localhost:6003/admin/email/find-user-by-email-for-send-email */
  adminEmailFindUserByEmailForSendEmail: `${appUrl}/admin/email/find-user-by-email-for-send-email`,
  /** POST http://localhost:6003/v1/admin/email/send-email-to-user */
  adminEmailSendToUser: `${appUrl}/v1/admin/email/send-email-to-user`,
  /** GET http://localhost:6003/admin/email/get-admin-email-list */
  adminEmailGetAdminEmailList: `${appUrl}/admin/email/get-admin-email-list`,

  /** GET/PUT http://localhost:3003/admin/zil-pricing — body: { usdt_price, zil_price } */
  adminZilPricing: `${appUrl}/admin/zil-pricing`,
  /** POST http://localhost:3003/admin/zil-pricing/convert — body: { usdt_amount } */
  adminZilPricingConvert: `${appUrl}/admin/zil-pricing/convert`,

  // ========= Support =========
  /** GET http://localhost:6003/v1/support/get-all-tickets — query: page, limit */
  supportGetAllTickets: `${appUrl}/v1/support/get-all-tickets`,
  /** POST http://localhost:6003/v1/support/update-ticket-status */
  supportUpdateTicketStatus: `${appUrl}/v1/support/update-ticket-status`,
  /** POST http://localhost:6003/v1/support/reply-ticket */
  supportReplyTicket: `${appUrl}/v1/support/reply-ticket`,

  // ========= Launchpad =========
  launchpadCurrencies: "launchpad/currencies",
  launchpadCreate: "launchpad/create",
  launchpadListing: "launchpad/all-launchpads-list",
  /** Backend expects POST http://localhost:6003/admin/launchpad/edit/:id */
  launchpadUpdate: (id) => `${appUrl}/admin/launchpad/edit/${id}`,
  launchpadUpcoming: "upcoming-launchpad-listing",
  launchpadLive: "live-launchpad-listing",
  launchpadEnded: "ended-launchpad-listing",
  launchpadCancelled: "cancelled-launchpad-listing",
  /** Legacy status-update API as provided by backend team */
  launchpadUpdateStatus: `${appUrl}/admin/launchpad/update-launchpad-status`,
  /** POST http://localhost:6003/v1/admin/launchpad/update-launchpad-fund-status — body: { id, status: FILLED | FAILED | CANCELLED } */
  launchpadUpdateFundStatus: `${appUrl}/v1/admin/launchpad/update-launchpad-fund-status`,
  /** GET http://localhost:6003/admin/launchpad/all-launchpads-stats */
  launchpadAllLaunchpadsStats: `${appUrl}/admin/launchpad/all-launchpads-stats`,
  launchpadSummary: "all-launchpads-summary",
  /** Aggregate counts for dashboard cards */
  launchpadCount: "launchpad/count",
  /** Per-launchpad purchase/participation transactions */
  launchpadTransactions: (launchpadId) => `launchpad/transactions/${launchpadId}`,

  // ========= Withdrawals (admin) =========
  /** GET — pending withdrawal requests */
  pendingWithdrawalRequest: "pending_withdrawal_request",
  /** POST — completed withdrawals (body: skip, limit) */
  completeWithdrawalRequest: "complete_withdrawal_request",
  /** POST — rejected withdrawals (body: skip, limit) */
  rejectWithdrawalRequest: "reject_withdrawal_request",
  /** POST — approve/reject pending withdrawal */
  updateWithdrawalStatus: "update_withdrawal_status",

  /** GET/POST /v1/admin/withdrawal-status — body: { withdrawal_status: boolean } */
  adminWithdrawalStatus: "withdrawal-status",

  /** GET — all crypto withdrawals (all statuses) */
  getWithdrawalHistory: `${appUrl}/v1/transaction/get_withdrawal_history`,
  /** GET — all INR withdrawals */
  getInrWithdrawalHistory: `${appUrl}/v1/transaction/get_inr_withdrawal_history`,
};
